import React, { useState, useEffect } from 'react';
import { Badge, Dropdown, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api from '../services/api';

// A nice pleasant notification sound (hosted or imported)
// Fallback to a reliable external URL if local not available, or use the base64 technique but use-sound manages it better.
const NOTIFY_SOUND_URL = '/notificationsound.mp3';
const audioInstance = new Audio(`${window.location.origin}${NOTIFY_SOUND_URL}`);

const NotificationCenter = () => {
    const [notifications, setNotifications] = useState([]);
    const [prevCount, setPrevCount] = useState(null);

    const playSound = () => {
        try {
            audioInstance.currentTime = 0; // Reset to start
            audioInstance.play().then(() => {
                console.log("Notification sound played successfully.");
            }).catch(err => {
                console.warn("Audio playback issue (likely browser policy):", err);
            });
        } catch (e) {
            console.error("Audio creation/playback failed:", e);
        }
    };

    const fetchNotifications = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            const res = await api.get('/notifications');

            const newNotifs = res.data;
            const newCount = newNotifs.length;

            // Sequential Audio Logic: Play once per NEW notification arriving AFTER initial load
            if (prevCount !== null && newCount > prevCount) {
                const addedCount = newCount - prevCount;
                console.log(`Sound Trigger: ${addedCount} new alerts detected.`);
                for (let i = 0; i < addedCount; i++) {
                    setTimeout(() => {
                        playSound();
                    }, i * 1500);
                }
            }

            setNotifications(newNotifs);
            setPrevCount(newCount);

        } catch (error) {
            console.error("Notification Polling Error", error);
        }
    };

    const handleClearAll = async () => {
        try {
            await api.put('/notifications/read-all');
            setNotifications([]);
            setPrevCount(0);
        } catch (error) {
            console.error("Clear All Failed", error);
        }
    };

    const markAsRead = async (id, e) => {
        e && e.stopPropagation();
        try {
            await api.put(`/notifications/${id}/read`);
            setNotifications(notifications.filter(n => n._id !== id));
            setPrevCount(c => Math.max(0, c - 1));
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 10000); // Poll every 10s
        return () => clearInterval(interval);
    }, []);

    return (
        <Dropdown align="end">
            <Dropdown.Toggle variant="light" className="position-relative border-0 bg-transparent p-2 verify-notification-toggle d-flex align-items-center justify-content-center shadow-none">
                <div className="position-relative">
                    <Icon icon="solar:bell-bold" className="text-2xl" style={{ color: 'var(--primary-color)' }} />
                    {notifications.length > 0 && (
                        <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-light" style={{ fontSize: '0.65rem', padding: '0.25em 0.5em' }}>
                            {notifications.length}
                            <span className="visually-hidden">unread messages</span>
                        </span>
                    )}
                </div>
            </Dropdown.Toggle>

            <Dropdown.Menu className="p-0 shadow-lg border-0 rounded-4 overflow-hidden" style={{ minWidth: '380px', maxWidth: '420px', zIndex: 99999 }}>
                <div className="text-white p-3 d-flex justify-content-between align-items-center" style={{ backgroundColor: 'var(--primary-color)' }}>
                    <div>
                        <div className="d-flex align-items-center gap-2">
                            <h6 className="mb-0 fw-bold">Notifications</h6>
                            <button
                                className="btn btn-sm p-0 text-white opacity-75 hover-opacity-100 border-0 bg-transparent"
                                onClick={(e) => { e.stopPropagation(); playSound(); }}
                                title="Test Sound"
                            >
                                <Icon icon="solar:volume-loud-bold" />
                            </button>
                        </div>
                        <small className="opacity-75">You have {notifications.length} unread alerts</small>
                    </div>
                    {notifications.length > 0 && (
                        <Button size="sm" variant="light" className="fw-bold text-xs py-1 px-3 rounded-pill bg-white text-dark border-0 shadow-sm" onClick={handleClearAll}>
                            Clear All
                        </Button>
                    )}
                </div>

                <div style={{ maxHeight: '350px', overflowY: 'auto', backgroundColor: '#f8f9fa' }}>
                    {notifications.length === 0 ? (
                        <div className="text-center p-5 text-muted">
                            <Icon icon="solar:bell-off-linear" className="text-5xl mb-3 opacity-25" />
                            <p className="fw-medium mb-1">No new notifications</p>
                            <small>We'll notify you when something arrives.</small>
                        </div>
                    ) : (
                        notifications.map((n) => (
                            <Dropdown.Item key={n._id} className="p-3 border-bottom d-flex align-items-start gap-3 bg-white" onClick={(e) => e.preventDefault()}>
                                <div className={`p-2 rounded-circle flex-shrink-0 d-flex align-items-center justify-content-center ${n.type === 'Emergency' ? 'bg-danger-subtle text-danger' :
                                    n.type === 'Queue' ? 'bg-warning-subtle text-warning' :
                                        n.type === 'CallNext' ? 'bg-success-subtle text-success' :
                                            'bg-primary-subtle text-primary'
                                    }`} style={{ width: '40px', height: '40px' }}>
                                    <Icon icon={
                                        n.type === 'Emergency' ? 'solar:danger-triangle-bold' :
                                            n.type === 'Queue' ? 'solar:clock-circle-bold' :
                                                n.type === 'CallNext' ? 'solar:megaphone-bold' :
                                                    'solar:calendar-add-bold'
                                    } className="text-xl" />
                                </div>
                                <div className="flex-grow-1">
                                    <div className="d-flex justify-content-between align-items-start mb-1">
                                        <span className={`fw-bold text-xs text-uppercase ${n.type === 'Emergency' ? 'text-danger' : ''}`} style={{ color: n.type === 'Emergency' ? '' : 'var(--primary-color)' }}>
                                            {n.type === 'Emergency' ? 'Urgent Alert' : n.type}
                                        </span>
                                        <small className="text-muted text-xs whitespace-nowrap ms-2">
                                            {new Date(n.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </small>
                                    </div>
                                    <p className="mb-0 text-wrap text-sm fw-medium lh-sm text-dark">{n.message}</p>
                                </div>
                                <Button
                                    variant="light"
                                    size="sm"
                                    className="p-0 rounded-circle text-secondary hover-primary flex-shrink-0"
                                    onClick={(e) => markAsRead(n._id, e)}
                                    title="Mark as Read"
                                    style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                    <Icon icon="solar:check-read-bold" />
                                </Button>
                            </Dropdown.Item>
                        ))
                    )}
                </div>
                {notifications.length > 0 && (
                    <div className="p-2 bg-light text-center border-top">
                        <small className="text-secondary">
                            Manage your notification preferences in settings
                        </small>
                    </div>
                )}
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default NotificationCenter;
